import { useTranslation } from 'react-i18next';

import type {
  OrganizationDetailStoresItem,
  OrganizationDetailStoresItemStatus,
} from '@nucleus-commerce/api-client/platform';
import { Card, CardDescription, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { Mono } from '@/shared/ui/mono';
import { StatusDot, type StatusTone } from '@/shared/ui/status-dot';

const TONES: Record<OrganizationDetailStoresItemStatus, StatusTone> = {
  draft: 'muted',
  active: 'success',
  paused: 'warning',
  closed: 'muted',
};

export interface StoresCardProps {
  stores: readonly OrganizationDetailStoresItem[];
  className?: string;
}

/** Every store the organization owns, from the detail payload. */
export function StoresCard({ stores, className }: StoresCardProps) {
  const { t } = useTranslation('organizations');
  return (
    <Card
      className={cn('gap-0 overflow-hidden py-0', className)}
      data-tour="platform.organizations.stores"
    >
      <CardHeader className="border-b px-5 py-4">
        <CardTitle>{t('detail.stores.title')}</CardTitle>
        <CardDescription>{t('detail.stores.count', { count: stores.length })}</CardDescription>
      </CardHeader>
      <Table className="text-[13px]" aria-label={t('detail.stores.title')}>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="h-10 pl-5 text-xs font-medium">
              {t('detail.stores.columns.name')}
            </TableHead>
            <TableHead className="h-10 text-xs font-medium">
              {t('detail.stores.columns.status')}
            </TableHead>
            <TableHead className="h-10 text-xs font-medium">
              {t('detail.stores.columns.currency')}
            </TableHead>
            <TableHead className="h-10 pr-5 text-xs font-medium">
              {t('detail.stores.columns.timezone')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {stores.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-muted-foreground h-20 text-center">
                {t('detail.stores.empty')}
              </TableCell>
            </TableRow>
          ) : (
            stores.map((store) => (
              <TableRow key={store.id}>
                <TableCell className="py-3 pl-5">
                  <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-2">
                    <span className="truncate font-medium">{store.name}</span>
                    <Mono className="truncate">{store.slug}</Mono>
                  </div>
                </TableCell>
                <TableCell className="py-3">
                  <StatusDot tone={TONES[store.status]} data-status={store.status}>
                    {t(`storeStatus.${store.status}`)}
                  </StatusDot>
                </TableCell>
                <TableCell className="py-3">
                  <Mono className="text-foreground">{store.defaultCurrency}</Mono>
                </TableCell>
                <TableCell className="text-muted-foreground py-3 pr-5 whitespace-nowrap">
                  {store.timezone}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Card>
  );
}
